import { CLINICAL_HISTORY_KEY, STANDARD_REQUEST_FIELDS_EN } from '@/lib/data/requestFields';
/**
 * Editor-side model + validation for a teleconsultation template's REQUEST side
 * (`report_template.request_fields`). Every rule here mirrors the backend's
 * single validator (`WebApi/src/Services/Teleconsultation/RequestFieldSchema.cs`)
 * so an invalid shape is caught before it can be POSTed, never after.
 */
export const MAX_REQUEST_FIELDS = 25;
export const MAX_REQUEST_OPTIONS = 30;
export const MAX_OPTION_LENGTH = 60;
export const MAX_LABEL_LENGTH = 80;
const MAX_HELP_TEXT_LENGTH = 200;
const MAX_PLACEHOLDER_LENGTH = 100;
const MAX_UNIT_LENGTH = 12;
// Module-internal since the key editor was removed: keys are derived, never
// typed, so no component needs the bound.
const MAX_KEY_LENGTH = 40;
export const REQUEST_FIELD_TYPES = [
    'text',
    'textarea',
    'select',
    'multiselect',
    'number',
    'boolean',
    'date',
];
/** Keys become JSON object keys in `request_values`, so they stay identifier-shaped. */
const REQUEST_FIELD_KEY_PATTERN = /^[a-zA-Z][a-zA-Z0-9_]{0,39}$/;
export function supportsOptions(type) {
    return type === 'select' || type === 'multiselect';
}
function supportsPlaceholder(type) {
    return type === 'text' || type === 'textarea' || type === 'number';
}
export function supportsNumberBounds(type) {
    return type === 'number';
}
let draftCounter = 0;
export function newRequestFieldDraft(overrides = {}) {
    draftCounter += 1;
    return {
        uid: `rf-${draftCounter}-${Date.now()}`,
        key: '',
        label: '',
        type: 'text',
        required: false,
        helpText: '',
        placeholder: '',
        options: [],
        unit: '',
        min: '',
        max: '',
        keyTouched: false,
        ...overrides,
    };
}
/**
 * "Murmur grade" -> "murmurGrade". Returns '' when the label has no letters to
 * work with; the key must start with a letter so leading digits are dropped.
 */
export function deriveFieldKey(label) {
    const words = label
        .replace(/[^a-zA-Z0-9]+/g, ' ')
        .trim()
        .split(/\s+/)
        .filter(Boolean);
    if (words.length === 0)
        return '';
    const camel = words
        .map((word, i) => i === 0 ? word.toLowerCase() : word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join('');
    const fromLetter = camel.replace(/^[^a-zA-Z]+/, '');
    if (!fromLetter)
        return '';
    return (fromLetter.charAt(0).toLowerCase() + fromLetter.slice(1)).slice(0, MAX_KEY_LENGTH);
}
/** Appends 2, 3, ... until the derived key no longer collides with `taken`. */
export function uniqueFieldKey(base, taken) {
    const used = new Set(taken);
    const root = base || 'field';
    if (!used.has(root))
        return root;
    for (let suffix = 2; suffix < 1000; suffix += 1) {
        const candidate = `${root.slice(0, MAX_KEY_LENGTH - String(suffix).length)}${suffix}`;
        if (!used.has(candidate))
            return candidate;
    }
    return root;
}
export const EMPTY_REQUEST_FIELDS_VALIDATION = {
    byUid: {},
    isValid: true,
};
function parseBound(raw) {
    const trimmed = raw.trim();
    if (!trimmed)
        return undefined;
    const value = Number(trimmed);
    return Number.isFinite(value) ? value : undefined;
}
function isBadBound(raw) {
    return raw.trim().length > 0 && parseBound(raw) === undefined;
}
export function validateRequestFields(drafts) {
    const byUid = {};
    const messages = [];
    const formError = drafts.length > MAX_REQUEST_FIELDS
        ? `A template may define at most ${MAX_REQUEST_FIELDS} request fields`
        : undefined;
    if (formError)
        messages.push(formError);
    const seenKeys = new Set();
    drafts.forEach((draft, index) => {
        const errors = {};
        const label = draft.label.trim();
        const key = draft.key.trim();
        const name = label || `Field ${index + 1}`;
        if (!label)
            errors.label = 'Label is required';
        else if (label.length > MAX_LABEL_LENGTH)
            errors.label = `Label must be ${MAX_LABEL_LENGTH} characters or fewer`;
        if (!key)
            errors.key = 'Key is required';
        else if (!REQUEST_FIELD_KEY_PATTERN.test(key))
            errors.key = `Key must start with a letter and use only letters, digits, or underscores (max ${MAX_KEY_LENGTH})`;
        else if (seenKeys.has(key))
            errors.key = `Key "${key}" is already used`;
        if (key)
            seenKeys.add(key);
        if (draft.helpText.trim().length > MAX_HELP_TEXT_LENGTH)
            errors.helpText = `Help text must be ${MAX_HELP_TEXT_LENGTH} characters or fewer`;
        if (supportsPlaceholder(draft.type) && draft.placeholder.trim().length > MAX_PLACEHOLDER_LENGTH)
            errors.placeholder = `Placeholder must be ${MAX_PLACEHOLDER_LENGTH} characters or fewer`;
        if (supportsOptions(draft.type)) {
            const options = draft.options.map((option) => option.trim());
            if (options.length === 0)
                errors.options = 'Add at least one option';
            else if (options.length > MAX_REQUEST_OPTIONS)
                errors.options = `Options must contain 1-${MAX_REQUEST_OPTIONS} entries`;
            else if (options.some((option) => !option || option.length > MAX_OPTION_LENGTH))
                errors.options = `Every option must be 1-${MAX_OPTION_LENGTH} characters`;
            else if (new Set(options).size !== options.length)
                errors.options = 'Options must be unique';
        }
        if (supportsNumberBounds(draft.type)) {
            if (draft.unit.trim().length > MAX_UNIT_LENGTH)
                errors.unit = `Unit must be ${MAX_UNIT_LENGTH} characters or fewer`;
            if (isBadBound(draft.min) || isBadBound(draft.max))
                errors.range = 'Min and max must be numbers';
            else {
                const min = parseBound(draft.min);
                const max = parseBound(draft.max);
                if (min !== undefined && max !== undefined && min > max)
                    errors.range = 'Min must be less than or equal to max';
            }
        }
        if (Object.keys(errors).length > 0) {
            byUid[draft.uid] = errors;
            for (const message of [
                errors.label,
                errors.key,
                errors.options,
                errors.range,
                errors.unit,
                errors.helpText,
                errors.placeholder,
            ]) {
                if (message)
                    messages.push(`${name}: ${message}`);
            }
        }
    });
    return {
        byUid,
        formError,
        isValid: messages.length === 0,
        firstMessage: messages[0],
    };
}
/**
 * Drafts -> the `requestFields` wire shape. Members the chosen type does not
 * support are dropped entirely rather than sent empty, because the backend
 * rejects (for example) a placeholder on a select field.
 */
export function serializeRequestFields(drafts) {
    return drafts.map((draft) => {
        const field = {
            key: draft.key.trim(),
            label: draft.label.trim(),
            type: draft.type,
            required: draft.required,
        };
        const helpText = draft.helpText.trim();
        if (helpText)
            field.helpText = helpText;
        if (supportsPlaceholder(draft.type)) {
            const placeholder = draft.placeholder.trim();
            if (placeholder)
                field.placeholder = placeholder;
        }
        if (supportsOptions(draft.type)) {
            field.options = draft.options.map((option) => option.trim()).filter(Boolean);
        }
        if (supportsNumberBounds(draft.type)) {
            const unit = draft.unit.trim();
            if (unit)
                field.unit = unit;
            const min = parseBound(draft.min);
            if (min !== undefined)
                field.min = min;
            const max = parseBound(draft.max);
            if (max !== undefined)
                field.max = max;
        }
        return field;
    });
}
/** Wire shape -> drafts, for editing a stored template. */
export function toRequestFieldDrafts(fields) {
    if (!fields)
        return [];
    return fields.map((field) => newRequestFieldDraft({
        key: field.key ?? '',
        label: field.label ?? '',
        type: field.type,
        required: field.required ?? false,
        helpText: field.helpText ?? '',
        placeholder: field.placeholder ?? '',
        options: field.options ? [...field.options] : [],
        unit: field.unit ?? '',
        min: field.min == null ? '' : String(field.min),
        max: field.max == null ? '' : String(field.max),
        keyTouched: true,
    }));
}
/**
 * A new teleconsultation template (or one with no request fields) starts from
 * Clinical History alone, the one question every consultation needs. The other
 * standard question, Specific Questions for Radiologist, is optional and is
 * added from the question menu, the way the Clinical tags section is on the
 * response side. Either can still be removed.
 */
export function seedRequestFieldDrafts() {
    return toRequestFieldDrafts(STANDARD_REQUEST_FIELDS_EN.filter((field) => field.key === CLINICAL_HISTORY_KEY));
}
/** Drafts for a template's request form: its own fields, else the seed. */
export function requestFormDrafts(fields) {
    return fields && fields.length > 0 ? toRequestFieldDrafts(fields) : seedRequestFieldDrafts();
}
/**
 * Adds one standard question by its reserved key, where it belongs: Clinical
 * History first, Specific Questions last. A no-op when the form already asks it.
 */
export function addStandardRequestField(drafts, key) {
    if (drafts.some((draft) => draft.key === key))
        return drafts;
    const field = STANDARD_REQUEST_FIELDS_EN.find((candidate) => candidate.key === key);
    if (!field)
        return drafts;
    const added = toRequestFieldDrafts([field]);
    return key === CLINICAL_HISTORY_KEY ? [...added, ...drafts] : [...drafts, ...added];
}
/** Swaps a field with its neighbour; order is positional (no `order` member). */
export function moveRequestField(drafts, uid, direction) {
    const index = drafts.findIndex((draft) => draft.uid === uid);
    const target = index + direction;
    if (index < 0 || target < 0 || target >= drafts.length)
        return drafts;
    const next = [...drafts];
    next[index] = drafts[target];
    next[target] = drafts[index];
    return next;
}
